import { PaginationState, SortingState } from '@tanstack/react-table';
import { endsWith, snakeCase } from 'lodash-es';
import {
  AUTH_MULTI_ACCOUNT_PREFIX,
  getChildType,
  getChildrenList,
  getParent,
  getParentBalance,
} from 'src/clients';
import {
  useCheckMAMViewOnlyChildType,
  useEnv,
  useProductActiveV2,
} from 'src/hooks';
import {
  MULTI_ACCOUNT_MANAGEMENT_CHILDREN_LIST_URL,
  MULTI_ACCOUNT_MANAGEMENT_PARENT_BALANCE_URL,
  MULTI_ACCOUNT_MANAGEMENT_PARENT_URL,
} from 'src/models';
import useSWR, { mutate } from 'swr';
import useSWRImmutable from 'swr/immutable';

type NewTableParam = {
  sorting?: SortingState;
  pagination?: PaginationState;
  fetchAll?: boolean;
};

const defaultPagination = {
  pageIndex: 0,
  pageSize: 100,
};

const defaultSorting = [
  {
    id: 'status',
    desc: true,
  },
];
const defaultGetParams = {
  sorting: defaultSorting,
  pagination: defaultPagination,
  fetchAll: false,
};

export const useChildTypeData = () => {
  const swr = useSWRImmutable(`${AUTH_MULTI_ACCOUNT_PREFIX}/child-type`, () =>
    getChildType().then(e => e.data.data),
  );

  return swr;
};

export const useParentData = () => {
  const swr = useSWRImmutable(MULTI_ACCOUNT_MANAGEMENT_PARENT_URL, () =>
    getParent().then(e => e.data.data),
  );

  return swr;
};

export const useChildTypeDataProd = () => {
  const swr = useSWR(
    `${AUTH_MULTI_ACCOUNT_PREFIX}/child-type/prod`,
    () => getChildType(true).then(e => e.data.data),
    {
      revalidateOnFocus: false,
    },
  );

  return swr;
};

export const useParentDataProd = () => {
  const swr = useSWR(
    `${MULTI_ACCOUNT_MANAGEMENT_PARENT_URL}/prod`,
    () => getParent(true).then(e => e.data.data),
    {
      revalidateOnFocus: false,
    },
  );

  return swr;
};

export const useGetChildrenList = ({
  sorting = defaultSorting,
  pagination = defaultPagination,
  fetchAll = false,
}: NewTableParam = defaultGetParams) => {
  const { isViewOnlyChildActive } = useCheckMAMViewOnlyChildType();
  const { checkActive, isCheckActiveLoading } = useProductActiveV2();
  const { currentEnv } = useEnv();
  const MAM_SUFFIX = '0bulkbalance';

  const { pageIndex = 0, pageSize = 1 } = pagination || {};
  const params = {
    order_by: sorting
      ?.map(value => `${snakeCase(value.id)}:${value.desc ? 'DESC' : 'ASC'}`)
      .join(','),
    limit: fetchAll ? -1 : pageSize,
    offset: pageIndex * pageSize,
  };

  const { data, ...props } = useSWRImmutable(
    !isCheckActiveLoading &&
      checkActive('MULTI_ACCOUNT_MANAGEMENT') &&
      !isViewOnlyChildActive
      ? [MULTI_ACCOUNT_MANAGEMENT_CHILDREN_LIST_URL, params, currentEnv]
      : null,
    () => getChildrenList(params).then(res => res.data.data),
    { revalidateOnMount: true },
  );

  const mutateAny = () => {
    return mutate(
      key =>
        Array.isArray(key) &&
        key[0] === MULTI_ACCOUNT_MANAGEMENT_CHILDREN_LIST_URL,
    );
  };

  const childrenData = data?.pagination?.data ?? [];
  const realChildren = childrenData.filter(
    child => !endsWith(child.username, MAM_SUFFIX),
  );

  const isMemTrulyActive = data?.totalChild == 0 || realChildren.length > 0;

  return {
    ...data?.pagination,
    data: realChildren ?? [],
    totalConnected: data?.totalChild ?? 0,
    totalChildWithNoKybData: data?.totalChildWithNoKybData ?? 0,
    ...props,
    isMemTrulyActive,
    mutateAny,
  };
};

export const useMultiAccountParentBalance = () => {
  const { data, isLoading, mutate } = useSWR(
    MULTI_ACCOUNT_MANAGEMENT_PARENT_BALANCE_URL,
    () => getParentBalance().then(res => res.data.data),
  );

  const defaultParentBalance = {
    availableBalance: 0,
    availableOverdraft: 0,
    balance: 0,
    pendingBalance: 0,
    freezeBalance: 0,
    totalChildAvailableBalance: 0,
    totalChildBalance: 0,
    totalChildOverdraftBalance: 0,
    totalChildPendingBalance: 0,
    totalChildFreezeBalance: 0,
    username: '',
  };

  return {
    data: data || defaultParentBalance,
    isLoading,
    triggerFetch: () => mutate(),
  };
};
